import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useOrganization } from "@/hooks/useOrganization";
import { useUserRole } from "@/hooks/useUserRole";
import { usePartnerRole } from "@/hooks/usePartnerRole";
import { Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface RoleGuardProps {
  children: ReactNode;
  allowedRoles: ("proprietario" | "gerente" | "vendedor" | "partner")[];
  redirectTo?: string;
}

export function RoleGuard({ children, allowedRoles, redirectTo = "/dashboard" }: RoleGuardProps) {
  const { currentOrgId, currentOrg, isLoading: orgLoading } = useOrganization();
  const { isAdmin, isLoading: adminLoading } = useUserRole();
  const { isPartnerAdmin, partnerId, isLoading: partnerLoading } = usePartnerRole();

  const { data: userRole, isLoading: roleLoading } = useQuery({
    queryKey: ["user-role", currentOrgId],
    queryFn: async () => {
      if (!currentOrgId) return null;
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data } = await supabase
        .from("org_members")
        .select("role")
        .eq("org_id", currentOrgId)
        .eq("user_id", user.id)
        .single();

      return data?.role || null;
    },
    enabled: !!currentOrgId,
  });

  const isLoading = orgLoading || roleLoading || adminLoading || partnerLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Admin sempre tem acesso total
  if (isAdmin) {
    return <>{children}</>;
  }

  // Partner tem acesso às organizações do seu partner
  const isPartnerOfOrg = isPartnerAdmin && currentOrg?.partner_id === partnerId;
  if (isPartnerOfOrg) {
    return <>{children}</>;
  }

  // Verificar se a role do usuário está permitida
  if (!userRole || !allowedRoles.includes(userRole as any)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
